import MeridianColumn from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import styled from '@emotion/styled';
import React, { useCallback, useMemo } from 'react';
import { TranslatedString, useTranslation } from '../components/blocks';
import { TruncatedText } from '../components/composites';
import { ItemImagePopover } from '../components/constructed';
import { ColumnFormat } from '../components/constructed/SuperPaginatedTable';
import {
    ResellerItem,
    DEFAULT_ITEM_IMAGE_URL,
    ASIN_MISSING_PLACEHOLDER,
    IAsynchronouslyLoadedValue,
} from '../redux/types';

const ItemImage = styled.img`
    display: block;
    margin-left: auto;
    margin-right: auto;
    width: 25em;
`;

export const useItemTableHelpers = (
    itemMap: Map<string, ResellerItem>,
    asinToImageUrls: IAsynchronouslyLoadedValue<Map<string, string>>,
    translationNamespace: string = 'returnOrderItemList'
) => {
    const { t } = useTranslation(translationNamespace);

    const createTextFormat = useCallback(
        (getText: (value: any) => string) => ({
            render: getText,
            sort: getText,
            search: getText,
        }),
        []
    );

    const getTitle = useCallback(
        (itemId: string): TranslatedString => {
            const title = itemMap.get(itemId)?.title;
            return title ? t.cognate(title) : t('title-value_missing');
        },
        [itemMap, t]
    );

    const getSku = useCallback(
        (itemId: string, type: 'shipping' | 'promotional' = 'shipping'): TranslatedString => {
            const item = itemMap.get(itemId);
            const sku = type === 'shipping' ? item?.externalSku : item?.promotionalSku;
            if (!sku || sku.startsWith('NO SKU')) {
                return t('externalSku-value_missing');
            }
            return t.cognate(sku);
        },
        [itemMap, t]
    );

    const getUpc = useCallback(
        (itemId: string): TranslatedString => {
            const upc = itemMap.get(itemId)?.upc;
            return upc ? t.cognate(upc) : t('upc-value_missing');
        },
        [itemMap, t]
    );

    const getAsin = useCallback(
        (itemId: string): TranslatedString => {
            const asin = itemMap.get(itemId)?.asin || t('asin-value_missing');
            return t.cognate(asin);
        },
        [itemMap, t]
    );

    const getItemImageUrlFromStore = useCallback(
        (itemId: string): string | undefined => {
            const asin = itemMap.get(itemId)?.asin || ASIN_MISSING_PLACEHOLDER;
            return asinToImageUrls.value?.get(asin) || DEFAULT_ITEM_IMAGE_URL;
        },
        [asinToImageUrls, itemMap]
    );

    const getSearchItemId = useCallback(
        (itemId: string): TranslatedString => {
            const itemIdValues = `${getTitle(itemId)} ${getSku(itemId)} ${getUpc(itemId)} ${getAsin(itemId)}`;
            return t.cognate(itemIdValues);
        },
        [getAsin, getUpc, getSku, getTitle, t]
    );

    const asinFormat = useMemo(() => createTextFormat(getAsin), [createTextFormat, getAsin]);
    const skuFormat = useMemo(() => createTextFormat((itemId) => getSku(itemId, 'shipping')), [
        createTextFormat,
        getSku,
    ]);
    const promoSkuFormat = useMemo(() => createTextFormat((itemId) => getSku(itemId, 'promotional')), [
        createTextFormat,
        getSku,
    ]);
    const upcFormat = useMemo(() => createTextFormat(getUpc), [createTextFormat, getUpc]);

    const titleFormat: ColumnFormat = useMemo(() => {
        const getText = (itemId: string) => getTitle(itemId);
        return {
            render: (itemId: string) => <TruncatedText text={getText(itemId)} maxWidth={25} />,
            sort: getText,
            search: getSearchItemId,
        };
    }, [getSearchItemId, getTitle]);

    const imageFormat: ColumnFormat = useMemo(() => {
        const getText = (itemId: string) => getTitle(itemId);
        return {
            render: (itemId: string) => {
                const translatedTitle = t.cognate(getText(itemId));
                return (
                    <ItemImagePopover
                        thumbnailUrl={getItemImageUrlFromStore(itemId)}
                        header={translatedTitle}
                        dataTestId={`${itemId}_ItemImagerPopover`}
                    >
                        <MeridianColumn alignmentHorizontal={'left'} alignmentVertical={'top'} spacing={'xxsmall'}>
                            <Text>{getSku(itemId)}</Text>
                            <ItemImage
                                src={getItemImageUrlFromStore(itemId)}
                                data-testid={`${itemId}-Image`}
                                aria-label={translatedTitle}
                            />
                        </MeridianColumn>
                    </ItemImagePopover>
                );
            },
            sort: getText,
            search: getText,
        };
    }, [getTitle, t, getSku, getItemImageUrlFromStore]);

    return {
        getTitle,
        getSku,
        getUpc,
        getAsin,
        getSearchItemId,
        getItemImageUrlFromStore,
        titleFormat,
        imageFormat,
        asinFormat,
        skuFormat,
        promoSkuFormat,
        upcFormat,
        createTextFormat,
    };
};
